/* Debounced auto-fetch for source names based on URL and source type. */

import { useState, useEffect, useRef } from "react";
import { useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { extractInstagramUsername } from "@/lib/extractInstagramUsername";

type SourceType = "youtube" | "rss" | "podcast" | "instagram";

const DEBOUNCE_MS = 500;

interface UseSourceNameFetchResult {
  fetchedName: string | null;
  isFetching: boolean;
}

export function useSourceNameFetch(
  url: string,
  sourceType: SourceType | null,
): UseSourceNameFetchResult {
  const [fetchedName, setFetchedName] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const getChannelName = useAction(api.sources.getChannelName);
  const getRSSFeedName = useAction(api.sources.getRSSFeedName);

  // Store actions in refs so the effect doesn't re-run when references change
  const getChannelNameRef = useRef(getChannelName);
  getChannelNameRef.current = getChannelName;
  const getRSSFeedNameRef = useRef(getRSSFeedName);
  getRSSFeedNameRef.current = getRSSFeedName;

  useEffect(() => {
    setFetchedName(null);
    setIsFetching(false);

    if (!url.trim() || !sourceType) {
      return;
    }

    if (sourceType === "instagram") {
      const username = extractInstagramUsername(url);
      setFetchedName(username);
      return;
    }

    let cancelled = false;

    const timeoutId = setTimeout(async () => {
      setIsFetching(true);
      try {
        let name: string | null = null;

        if (sourceType === "youtube") {
          name = await getChannelNameRef.current({ url: url.trim() });
        } else if (sourceType === "rss" || sourceType === "podcast") {
          name = await getRSSFeedNameRef.current({ url: url.trim() });
        }

        if (!cancelled) {
          setFetchedName(name);
        }
      } catch (error) {
        console.error("Error fetching source name:", error);
        if (!cancelled) {
          setFetchedName(null);
        }
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [url, sourceType]);

  return { fetchedName, isFetching };
}
